import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { ExamsListTable } from "../components/exams/ExamsListTable";
import { quizService } from "../services/quizService";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { useToast } from "../components/ui/ToastContainer";
import { parseApiError } from "../utils/errorHandler";
import { Plus, Search, RefreshCw } from "lucide-react";

export default function ExamsPage() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [exams, setExams] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 10;

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchExams(1, searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchExams = async (page = 1, search = "") => {
    setLoading(true);
    try {
      const response = await quizService.getAllQuizzes({
        page,
        limit: pageSize,
        search,
        sortBy: "createdAt",
        sortOrder: "desc",
      });

      if (response && response.status === "success") {
        const res = response as any;
        setExams(res.data.quizzes || []);
        setTotalItems(res.pagination.totalQuizzes || 0);
        setTotalPages(res.pagination.totalPages || 1);
        setCurrentPage(res.pagination.currentPage || 1);
      }
    } catch (error) {
      console.error("Failed to fetch exams:", error);
      const apiError = parseApiError(error);
      showError(apiError.message, "Failed to Load Exams");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (exam: any) => {
    if (window.confirm(`Are you sure you want to delete "${exam.title}"?`)) {
      try {
        const response = await quizService.deleteQuiz(exam._id);
        if (response.status === "success") {
          showSuccess(`Exam "${exam.title}" deleted successfully`, "Success");
          fetchExams(currentPage, searchTerm);
        }
      } catch (error) {
        console.error("Delete failed:", error);
        const apiError = parseApiError(error);
        showError(apiError.message, "Delete Failed");
      }
    }
  };

  const handleTogglePublish = async (exam: any) => {
    try {
      const response = await quizService.togglePublishQuiz(exam._id);
      if (
        response &&
        (response.status === "success" || response.status === "true")
      ) {
        // Update local state instead of refetching for better UX
        setExams((prev) =>
          prev.map((e) =>
            e._id === exam._id ? { ...e, isPublished: !e.isPublished } : e
          )
        );
        showSuccess(
          `Exam ${exam.isPublished ? "unpublished" : "published"} successfully`,
          "Success"
        );
      }
    } catch (error) {
      console.error("Failed to toggle publish:", error);
      const apiError = parseApiError(error);
      showError(apiError.message, "Publish Failed");
    }
  };

  const handleEdit = (exam: any) => {
    navigate(`/exams/${exam._id}/edit`);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-1">
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">
            Exams
          </h2>
          <p className="text-gray-500">Manage all quizzes and exams.</p>
        </div>

        <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
          <div className="w-full xl:w-[400px]">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={18}
              />
              <Input
                placeholder="Search exams..."
                className="pl-10 bg-white w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full xl:w-auto">
            <Button
              variant="outline"
              onClick={() => fetchExams(currentPage, searchTerm)}
              className="flex items-center justify-center gap-2 h-[44px] sm:h-[44px]"
            >
              <RefreshCw size={16} />
              Refresh
            </Button>

            <Link to="/exams/new" className="w-full sm:w-auto">
              <Button className="w-full bg-[#102040] hover:bg-[#102040] text-white flex items-center justify-center gap-2 h-[48px] sm:h-[44px]">
                <Plus size={18} />
                Create New Exam
              </Button>
            </Link>
          </div>
        </div>

        <ExamsListTable
          exams={exams}
          isLoading={loading}
          totalItems={totalItems}
          currentPage={currentPage}
          pageSize={pageSize}
          totalPages={totalPages}
          onPageChange={(page) => fetchExams(page, searchTerm)}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onTogglePublish={handleTogglePublish}
        />
      </div>
    </DashboardLayout>
  );
}
